import type { Product } from 'payments-api/model';
import {
  useGetRolesSuspense,
  usePostRoles,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import {
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';
import { Toast } from '@fragment/ui/src/components/Toast/Toast';

import { CatalogProductTable } from './CatalogProductTable';
import { CatalogUserTable } from './CatalogUserTable';

const NEW_ROLE_TAB_VALUE = '__new-role__';

const NewRoleModal = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (role: string) => void;
}) => {
  const [roleInput, setRoleInput] = useState('');
  const [createRoleError, setCreateRoleError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const createRoleMutation = usePostRoles(undefined, queryClient);
  const [isPending, setIsPending] = useState(false);
  const handleSubmit = useCallback(async () => {
    try {
      setIsPending(true);
      await createRoleMutation.mutateAsync({
        data: { role: roleInput.trim() },
      });
      await queryClient.invalidateQueries({ queryKey: ['/roles'] });
      onSubmit(roleInput.trim());
      setIsPending(false);
    } catch (err) {
      setIsPending(false);
      setCreateRoleError(
        err instanceof Error ? err.message : 'Failed to create role'
      );
    }
  }, [createRoleMutation, queryClient, onSubmit, roleInput]);
  return (
    <Modal
      width="sm"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      body={
        <div className="flex flex-col space-y-f4 font-sans">
          <span className="text-main text-md w-full text-left h-fit">
            {`Roles are categories of users and products like "Vendor", "Platform",
            "Customer", etc.`}
          </span>
          <ModalTextInput
            placeholder="Role name"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
          />
        </div>
      }
      footer={
        <div className="flex flex-row justify-between">
          {createRoleError && <ErrorText>{createRoleError}</ErrorText>}
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            onCancel={onClose}
            submitButtonLabel={isPending ? 'Creating...' : 'Create'}
            disabled={isPending || !roleInput.trim()}
          />
        </div>
      }
    />
  );
};

const RolesTabs = ({
  selectedRoleName,
  setSelectedRoleName,
}: {
  selectedRoleName: string;
  setSelectedRoleName: (roleName: string) => void;
}) => {
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const { data: rolesData } = useGetRolesSuspense();

  const roles = rolesData.data;
  const roleFilterOptions = useMemo(
    () =>
      roles
        .map((r) => ({ value: r.role, label: capitalize(r.role) }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [roles]
  );
  useEffect(() => {
    if (roleFilterOptions.length > 0 && selectedRoleName === '') {
      setSelectedRoleName(roleFilterOptions[0].value);
    }
  }, [roleFilterOptions, setSelectedRoleName, selectedRoleName]);

  const onCreateRole = useCallback(
    (roleName: string) => {
      setShowNewRoleModal(false);
      setSelectedRoleName(roleName);
    },
    [setShowNewRoleModal, setSelectedRoleName]
  );

  return (
    <>
      <TabsV2
        options={[
          ...roleFilterOptions,
          { value: NEW_ROLE_TAB_VALUE, label: 'New+' },
        ]}
        selectedValue={selectedRoleName}
        onClick={(value) => {
          if (value === NEW_ROLE_TAB_VALUE) {
            setShowNewRoleModal(true);
          } else {
            setSelectedRoleName(value);
          }
        }}
      />
      {showNewRoleModal && (
        <NewRoleModal
          onClose={() => {
            setShowNewRoleModal(false);
          }}
          onSubmit={onCreateRole}
        />
      )}
    </>
  );
};

type Props = {
  workspaceId: string;
};

const CatalogListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Catalog');

  const { showToast } = useToast();

  const [selectedRoleName, setSelectedRoleName] = useState<string>('');

  const onProductClick = useCallback(
    (_product: Product) => {
      showToast(
        <Toast type="success" message="Product detail page coming soon" />
      );
    },
    [showToast]
  );

  return (
    <PaymentViewLayout title="Catalog">
      <LayoutRow height="fit">
        <Suspense fallback="Loading roles...">
          <RolesTabs
            selectedRoleName={selectedRoleName}
            setSelectedRoleName={setSelectedRoleName}
          />
        </Suspense>
      </LayoutRow>
      <div className="flex flex-col w-full h-full">
        {selectedRoleName.trim() === '' && (
          <LayoutRow height="fit">
            <TextCell
              border="none"
              textSize="md"
              align="left"
              fullWidth
              text="Create a role to get started."
            />
          </LayoutRow>
        )}
        {selectedRoleName.trim() !== '' && (
          <div className="grid grid-cols-2 gap-x-f2 bg-canvas p-f2 w-full border-main border-t-0 border-[1px] h-full min-h-full">
            <CatalogUserTable
              workspaceId={workspaceId}
              selectedRoleName={selectedRoleName}
            />
            <CatalogProductTable
              selectedRoleName={selectedRoleName}
              onProductClick={onProductClick}
            />
          </div>
        )}
      </div>
    </PaymentViewLayout>
  );
};

export const CatalogPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading catalog..." />}>
    <CatalogListPageContent workspaceId={workspaceId} />
  </Suspense>
);
